import { Children, Fragment, cloneElement, isValidElement, useRef, type ReactElement, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';

type Align = 'center' | 'start';

type DescribedControlProps = {
  children?: ReactNode;
  'aria-describedby'?: string;
};

let nextHelpId = 0;

export interface SettingsRowProps {
  label: string;
  help?: string;
  required?: boolean;
  /** Id del control; si existe, la fila se comporta como etiqueta clicable. */
  htmlFor?: string;
  /** `start` para controles altos, como descripciones o portadas. */
  align?: Align;
  children: ReactNode | ((context: { describedById?: string }) => ReactNode);
}

const Grid = styled.div<{ $align: Align }>(({ $align }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 18.75rem) minmax(0, 1fr)',
  gap: 'clamp(0.875rem, 2.5vw, 1.5rem)',
  alignItems: $align === 'start' ? 'start' : 'center',
  padding: '1.375rem clamp(1rem, 3vw, 1.625rem)',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  [`@media (max-width: ${t.breakpoint.md})`]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '0.5rem',
    alignItems: 'stretch',
  },
}));

const LabelBlock = styled.div({
  minWidth: 0,
});

const Label = styled.span({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '0.25rem',
  fontFamily: t.font,
  fontSize: '0.875rem',
  fontWeight: 600,
  color: t.color.titleStrong,
});

const Help = styled.span({
  display: 'block',
  marginTop: '0.1875rem',
  fontFamily: t.font,
  fontSize: '0.78125rem',
  lineHeight: 1.45,
  color: t.color.muted,
});

const Required = styled.span({
  color: t.color.danger,
});

const Control = styled.div({
  minWidth: 0,
});

const mergeDescribedBy = (current: string | undefined, next: string | undefined) =>
  Array.from(new Set([current, next].filter(Boolean).flatMap(value => String(value).split(' ')))).join(' ') ||
  undefined;

const linkDescribedBy = (control: ReactNode, describedById: string | undefined): ReactNode => {
  if (!describedById || !isValidElement<DescribedControlProps>(control)) {
    return control;
  }

  const children = control.props.children
    ? Children.map(control.props.children, child => linkDescribedBy(child, describedById))
    : control.props.children;

  if (control.type === Fragment) {
    return cloneElement(control as ReactElement<DescribedControlProps>, {}, children);
  }

  return cloneElement(
    control as ReactElement<DescribedControlProps>,
    {
      'aria-describedby': mergeDescribedBy(control.props['aria-describedby'], describedById),
    },
    children
  );
};

/**
 * Fila de ajustes con etiqueta, ayuda y control alineado en dos columnas.
 */
const SettingsRow = ({ label, help, required = false, htmlFor, align = 'center', children }: SettingsRowProps) => {
  const helpIdRef = useRef<string | null>(null);

  if (!helpIdRef.current) {
    nextHelpId += 1;
    helpIdRef.current = `settings-row-help-${nextHelpId}`;
  }

  const helpId = help ? helpIdRef.current : undefined;
  const content = typeof children === 'function' ? children({ describedById: helpId }) : children;
  const control = linkDescribedBy(content, helpId);

  return (
    <Grid as={htmlFor ? 'label' : 'div'} htmlFor={htmlFor} $align={align}>
      <LabelBlock>
        <Label>
          {label}
          {required ? <Required title="obligatorio">*</Required> : null}
        </Label>
        {help ? (
          <Help id={helpId} data-testid="settings-row-help">
            {help}
          </Help>
        ) : null}
      </LabelBlock>
      <Control>{control}</Control>
    </Grid>
  );
};

export default SettingsRow;
